import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface LogoSettings {
  id: string;
  logo_text: string;
  font_size_mobile: string;
  font_size_desktop: string;
}

interface LogoSettingsContextType {
  logoSettings: LogoSettings | null;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

const LogoSettingsContext = createContext<LogoSettingsContextType | undefined>(undefined);

export const LogoSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const [logoSettings, setLogoSettings] = useState<LogoSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('site_logo_settings')
        .select('*')
        .limit(1)
        .single();

      if (error) throw error;
      setLogoSettings(data);
    } catch (error) {
      console.error('Failed to fetch logo settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Listen for updates from admin
  useEffect(() => {
    const onLogoUpdate = () => fetchSettings();
    window.addEventListener('logoSettingsUpdated', onLogoUpdate);
    return () => window.removeEventListener('logoSettingsUpdated', onLogoUpdate);
  }, [fetchSettings]);

  return (
    <LogoSettingsContext.Provider value={{ logoSettings, loading, refreshSettings: fetchSettings }}>
      {children}
    </LogoSettingsContext.Provider>
  );
};

export const useLogoSettings = () => {
  const context = useContext(LogoSettingsContext);
  if (context === undefined) {
    throw new Error('useLogoSettings must be used within a LogoSettingsProvider');
  }
  return context;
};
